import { groupBySection, packColumns } from '../paginate.js';
import type { MenuSection } from '../types.js';
import { bilingual, fitScale, hasTitle, listColumns, listCss, type MenuTemplate, type RenderContext } from './common.js';
import {
  NB_FAMILY,
  NB_THEME,
  NB_VARIANTS,
  nbArt,
  nbBlock,
  nbBothClass,
  nbColumns,
  nbCss,
  nbListScale,
} from './night-bites-columns.js';

/**
 * Night Bites, spotlight: the left third is a poster for the screen, a giant line-art icon of
 * its first section (or that section's picture in a round frame) over the big white title and
 * the script tagline, with the brand underneath. The list fills the right two thirds in two
 * columns with small inline icons. Portrait turns the poster into a band across the top.
 */

const S = '.mb-t-night-bites-spotlight';

/** The section the poster shows: the first titled one on this page, else the board's first. */
function spotlight(ctx: RenderContext): MenuSection | null {
  const blocks = ctx.page.columns.flat();
  return (
    blocks.find((b) => hasTitle(b.section))?.section ??
    ctx.board.sections.find((s) => hasTitle(s)) ??
    blocks[0]?.section ??
    null
  );
}

function poster(ctx: RenderContext): string {
  const { board } = ctx;
  const section = spotlight(ctx);
  const title = board.settings.title ?? board.name;
  const portrait = ctx.orientation === 'portrait';
  const scale = fitScale(ctx.showEn ? title : board.settings.titleAr || title, portrait ? 11 : 8, 0.4);
  const art = section ? nbArt(ctx, section, 'nbc-art') : '';
  return [
    '<div class="nbc-poster">',
    art ? `<div class="nbc-artbox">${art}</div>` : '',
    '<div class="nbc-ptext">',
    `<div class="nbc-titlebox" style="font-size:${scale.toFixed(3)}em">`,
    bilingual(ctx, title, board.settings.titleAr, 'nbc-title', 'h1'),
    '</div>',
    bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'nbc-tag'),
    `<div class="nbc-brandline">${bilingual(ctx, board.brand.name, board.brand.nameAr, 'nbc-brand', 'span')}</div>`,
    '</div></div>',
  ].join('');
}

export const nightBitesSpotlight: MenuTemplate = {
  id: 'night-bites-spotlight',
  name: 'Night Bites · Spotlight',
  description:
    'A giant line-art icon (or your section picture) and the big title on the left third, the list in two columns on the right. Matches the other Night Bites screens.',
  defaultTheme: NB_THEME,
  variants: NB_VARIANTS,
  cuisines: ['drinks', 'cafe', 'burgers'],
  family: { ...NB_FAMILY, layout: 'Spotlight' },
  uses: { featured: false, sectionImages: true, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(
      groupBySection(board),
      nbColumns(board, listColumns(board, {
        count: 2,
        // Portrait lists are set 8% larger, so they hold fewer of these em.
        capacity: (portrait ? 74 : 59) / nbListScale(board),
        heading: 4.4,
        item: { nameEm: 1.6, descEm: 1, perLine: 44, gap: 1.5 },
      }), { sizeRow: 1.5 * 1.25, nameLine: 1.6 * 1.15, nameChars: portrait ? 14 : 16 }),
      { balance: true },
    );
  },

  render(ctx) {
    const cols = ctx.page.columns
      .map((column) => `<div class="nbc-col">${column.map((b) => nbBlock(ctx, b, 'nbc', { leader: true, art: true })).join('')}</div>`)
      .join('');
    return `<div class="nbc-wrap">${poster(ctx)}<div class="nbc-cols${nbBothClass(ctx)}">${cols}</div></div>`;
  },

  css: `
${S}{padding:0}
${nbCss(S, 'nbc')}
${S} .nbc-wrap{flex:1;min-height:0;display:grid;grid-template-columns:34% 1fr}
${S}.mb-portrait .nbc-wrap{grid-template-columns:1fr;grid-template-rows:auto 1fr}
${S} .nbc-poster{min-width:0;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;
  gap:2.4em;padding:3em 3em 4em;background:var(--mb-surface);position:relative}
${S} .nbc-poster::after{content:'';position:absolute;top:3em;bottom:3em;inset-inline-end:0;border-inline-end:.09em solid var(--nb-rule)}
${S}.mb-portrait .nbc-poster{flex-direction:row;justify-content:flex-start;text-align:start;gap:3.6em;padding:4.4em 4em 4em}
${S}.mb-portrait .nbc-poster::after{top:auto;bottom:0;left:3em;right:3em;border-inline-end:0;border-bottom:.09em solid var(--nb-rule)}
${S} .nbc-artbox{flex-shrink:0}
${S} .nbc-art{width:17em;height:17em}
${S}.mb-portrait .nbc-art{width:19em;height:19em}
${S}.mb-portrait .nbc-title{font-size:8em}
${S} .nbc-art-photo{border-width:.25em;padding:.5em}
${S} .nbc-ptext{min-width:0;max-width:100%}
${S} .nbc-title{font-size:6.4em}
${S} .nbc-title.mb-2nd{font-size:2.8em;margin-top:.15em}
${S} .nbc-tag{font-size:1.9em;margin-top:.3em}
${S} .nbc-tag.mb-2nd{font-size:1.5em;margin-top:0}
${S} .nbc-brandline{display:flex;flex-wrap:wrap;justify-content:center;gap:.3em .9em;margin-top:2.2em;color:var(--mb-accent);
  font-size:1em;letter-spacing:.3em;text-transform:uppercase}
${S}.mb-portrait .nbc-brandline{justify-content:flex-start;margin-top:1.4em}
${S} .nbc-brand.mb-ar{letter-spacing:0}
${S} .nbc-cols{min-width:0;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:4em;padding:3.6em 4em 3em}
${S} .nbc-cols.nb-both{font-size:.88em}
${S}.mb-portrait .nbc-cols{padding:3em 3.5em 3.4em;gap:3em;font-size:1.08em}
${S}.mb-portrait .nbc-cols.nb-both{font-size:.95em}
${S} .nbc-col{min-width:0;display:flex;flex-direction:column;gap:2.2em}
${S} .nbc-head{display:flex;align-items:center;gap:.9em;margin-bottom:1.3em;padding-bottom:.6em;
  border-bottom:.1em solid color-mix(in srgb,var(--mb-accent) 50%,transparent)}
${S} .nbc-head .nbc-art{width:3.6em;height:3.6em;flex-shrink:0}
${S} .nbc-htext{min-width:0}
${S} .nbc-st{font-size:2.6em;line-height:1.15}
${S} .nbc-st.mb-2nd{font-size:1.8em}
${S} .nbc-cont .nbc-st{font-size:2.1em}
${listCss(S, 'nbc', { name: '1.6em', price: '1.5em', desc: '1em', leader: '.13em dotted color-mix(in srgb,var(--mb-text) 30%,transparent)' })}
${S} .nbc-item{margin-bottom:1.5em}
${S} .nbc-name2{font-size:1.1em}
`,
};
